// The two local caches the look resolver reads (themes.md §8, §11).
//
// **The entitlement set.** The client keeps the last set of entitlement
// keys `/api/me` answered with, beside the settings. `net/api.ts` writes it
// on every `getMe`, so the hub, the game entry, the landing screen and the
// arcade page all refresh it, and the game entry resolves a look with no
// second `/api/me` call (R809).
//
// **The custom theme bodies.** A custom theme is cached by its theme code
// AND its category (`themeKey`), with its version, so the game starts with
// the look of the last visit and needs no network. One code names a theme
// in each category its creator made (R819), so the code alone is no key. A
// code the meta server no longer knows in a category is marked `missing`:
// its body stays in the cache, and the resolver shows AUTO for that category
// until a later lookup finds the pair again.
//
// A cache that a client before R819 wrote is keyed by the code alone. Every
// entry carries its own code and category, so the read keys each entry
// again from those two fields, and the next write stores the new keys.
//
// Every read and write goes through try/catch, in the shape of
// `ui/settings.ts`: a private window or a full store only means that the
// cache forgets. This module imports nothing from `net/`, so `net/api.ts`
// can write the entitlement set without an import cycle.

import type { ThemeCategory } from '../gen/balance';
import { isObject } from '../net/json';
import { readStore, writeStore } from './localstore';

export const ENTITLEMENTS_KEY = 'aquaphobia.entitlements';
export const THEME_BODIES_KEY = 'aquaphobia.themes';

/** The account that made a custom theme, or null when it has no owner. */
export interface ThemeOwnerView {
  publicId: string;
  name: string;
}

/** One cached custom theme, in the shape `GET /api/themes/{code}/{category}`
 *  answers. */
export interface CachedTheme {
  code: string;
  category: ThemeCategory;
  version: number;
  body: unknown;
  owner: ThemeOwnerView | null;
  updatedAt: number;
  /** True after the meta server answered 404 for the code. The body stays,
   *  and the resolver shows AUTO while this is set. */
  missing?: boolean;
}

// --- the entitlement set ----------------------------------------------------

/** Stores the keys the account holds now. `getMe` calls this. */
export function rememberEntitlements(keys: Iterable<string>): void {
  const list = [...new Set([...keys].filter((k) => typeof k === 'string' && k !== ''))].sort();
  writeStore(ENTITLEMENTS_KEY, JSON.stringify(list));
}

/** The last entitlement set seen. Empty when none was ever stored. */
export function cachedEntitlements(): Set<string> {
  const raw = readStore(ENTITLEMENTS_KEY);
  if (!raw) return new Set();
  try {
    const v = JSON.parse(raw) as unknown;
    return new Set(Array.isArray(v) ? v.filter((k): k is string => typeof k === 'string') : []);
  } catch {
    return new Set();
  }
}

// --- the custom theme bodies ------------------------------------------------

/** The cache key of one custom theme: its code and its category (R819). */
export function themeKey(code: string, category: ThemeCategory): string {
  return `${code}/${category}`;
}

/** Every cached custom theme, by `themeKey`. A malformed entry is dropped,
 *  and an entry of the old code-only key is keyed again from its fields. */
export function cachedThemes(): Record<string, CachedTheme> {
  const raw = readStore(THEME_BODIES_KEY);
  const out: Record<string, CachedTheme> = {};
  if (!raw) return out;
  try {
    const v = JSON.parse(raw) as unknown;
    if (!isObject(v)) return out;
    for (const t of Object.values(v)) {
      if (!isObject(t)) continue;
      const e = t as Partial<CachedTheme>;
      if (typeof e.code !== 'string' || typeof e.category !== 'string') continue;
      out[themeKey(e.code, e.category)] = {
        code: e.code,
        category: e.category,
        version: typeof e.version === 'number' ? e.version : 0,
        body: e.body,
        owner: e.owner ?? null,
        updatedAt: typeof e.updatedAt === 'number' ? e.updatedAt : 0,
        ...(e.missing ? { missing: true } : {}),
      };
    }
  } catch { /* garbage in the store: nothing is cached */ }
  return out;
}

/** The cached theme of one code and category, or undefined. */
export function cachedTheme(code: string, category: ThemeCategory): CachedTheme | undefined {
  return code === '' ? undefined : cachedThemes()[themeKey(code, category)];
}

/** Writes the whole theme cache. */
function writeThemes(all: Record<string, CachedTheme>): void {
  writeStore(THEME_BODIES_KEY, JSON.stringify(all));
}

/** Stores one custom theme and clears its `missing` mark. */
export function storeTheme(t: CachedTheme): void {
  const all = cachedThemes();
  const { missing: _drop, ...kept } = t;
  all[themeKey(t.code, t.category)] = kept;
  writeThemes(all);
}

/**
 * Marks one code as unknown to the meta server in one category. The cached
 * body stays: a lookup that fails for a moment must not throw away the look
 * (R809).
 */
export function markThemeMissing(code: string, category: ThemeCategory): void {
  const all = cachedThemes();
  const key = themeKey(code, category);
  const had = all[key];
  all[key] = had
    ? { ...had, missing: true }
    : { code, category, version: 0, body: null, owner: null, updatedAt: 0, missing: true };
  writeThemes(all);
}

/**
 * Keeps the entries of the keys (`themeKey`) the settings still name, and
 * drops the rest. The cache is bounded by the six codes of the settings and
 * the few codes a player looked up in this session.
 */
export function pruneThemes(keep: Iterable<string>): void {
  const want = new Set(keep);
  const all = cachedThemes();
  let changed = false;
  for (const code of Object.keys(all)) {
    if (!want.has(code)) { delete all[code]; changed = true; }
  }
  if (changed) writeThemes(all);
}
